import { Controller } from "@hotwired/stimulus"

// Collapses everything but the country + search until the user has either
// picked a company from the register or explicitly chosen manual entry.
// An existing record opens expanded: a new client is SEARCHED for, an existing
// one is COMPARED, so they are two states of one screen.
//
// Learns about a registry pick via the "registry:selected" CustomEvent that
// legal_entity_controller#selectOption dispatches on `document` — the same
// document-level pub/sub already used for cross-controller signalling
// elsewhere in this app (see invoices/select_contact_controller's
// "selectedContact"/"changeContact" pair), rather than a bespoke channel.
export default class extends Controller {
  static targets = ["body", "escapes"]
  static values = { open: { type: Boolean, default: false } }

  connect() {
    this.onSelected = () => this.reveal()
    document.addEventListener("registry:selected", this.onSelected)
    // FR-6: picking a country we have no register for means manual entry is the
    // only path left, so the form opens for it. Without this the modal showed
    // "we have no register for this country — enter the details manually" over
    // a collapsed form, and the user still had to click "enter manually" to get
    // the fields we had just told them were their only option.
    this.onManualRequired = () => this.reveal()
    document.addEventListener("registry:manual-required", this.onManualRequired)
    this.#apply(this.openValue)
  }

  disconnect() {
    document.removeEventListener("registry:selected", this.onSelected)
    document.removeEventListener("registry:manual-required", this.onManualRequired)
  }

  reveal() {
    this.#apply(true)
  }

  revealManual() {
    this.#apply(true)
  }

  // Reveal first, dispatch second. The VAT section used to carry BOTH a "body"
  // target and an entity-type-toggle "vatSection" one, so #apply un-hid exactly
  // what entity-type-toggle had just hidden and choosing "physical person"
  // revealed VAT-payer fields that cannot apply to a person. The markup now
  // gives each controller its own element — a "body" wrapper around an inner
  // "vatSection" (see _contact_modal_content.html.erb) — so neither can
  // overwrite the other whatever order they run in; this order just keeps the
  // entity-type decision the newer one if that separation ever regresses.
  revealPerson() {
    this.#apply(true)
    const personRadio = this.element.querySelector("input[name='entity_type'][value='physical_person']")
    if (personRadio) {
      personRadio.checked = true
      personRadio.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  #apply(open) {
    this.openValue = open
    // Plural: the revealed body is not one contiguous element — it's every
    // piece of the "Client data" fieldset beyond country + search (entity
    // type, IČO, DIČ/VAT) plus the Address/Postal/Contact/Other fieldsets,
    // each tagged with the same target name so the original single-legend
    // fieldset and its 6/6 column pairs stay intact instead of being split.
    this.bodyTargets.forEach((el) => el.classList.toggle("d-none", !open))
    if (this.hasEscapesTarget) this.escapesTarget.classList.toggle("d-none", open)
  }
}
